import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import { BlogDetailsPage, ImageAsset as TImageAsset } from '@/types/generated';
import RichText, { RichTextContent } from '@/components/RichText';
import TAGButton from '@/components/TAGButton';
import { BlogImage } from '@/components/BlogDetailsPage';
import { Maybe } from '@/types';
import { useAppContext } from '@/context';
import TAGLink from '@/components/TAGLink';

import BlogImageCondition from '../BlogImageCondition';

import {
	blogHeroWrapperStyles,
	buttonStyles,
	mainPhotoRichTextDocProps,
	mainPhotoStyles,
	mainPhotoTextOverlayStyles,
	mainPhotoWrapperStyles,
	remainingPhotosContainerWrapperStyles,
	smallPhotoRichTextDocProps,
	smallPhotoTextOverlayStyles,
	smallPhotoWrapperStyles,
} from './BlogsListHero.styles';

export interface IBlogsListHero {
	blogsList: Array<BlogDetailsPage> | null | undefined;
	buttonLabel: Maybe<string> | undefined;
}

const BlogsListHero = ({ blogsList, buttonLabel }: IBlogsListHero) => {
	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	const { config } = useAppContext();
	const blogRootPath = config.features.blogs.root;

	if (!blogsList) {
		return null;
	}

	const mainBlog = blogsList[0];
	const remainingBlogs = blogsList.slice(1, 4);
	const path = mainBlog?.path ?? '';
	const blogImage = mainBlog?.blogImage as BlogImage;
	const imageAsset = mainBlog?.imageAsset as TImageAsset;
	const blogItemLink = `${blogRootPath}${path}/`;
	const mainBlogSummary = mainBlog?.summary?.json as RichTextContent;

	return (
		<Box data-test-id="blog_hero_wrapper" sx={blogHeroWrapperStyles}>
			{/* Main Photo Section */}
			<Box sx={mainPhotoWrapperStyles} data-test-id="blog_hero_main_photo">
				<BlogImageCondition
					imageAsset={imageAsset}
					imageAssetStyles={mainPhotoStyles}
					blogImage={blogImage}
					blogImageStyles={mainPhotoStyles}
					dataTestId={
						imageAsset ? `blog_hero_main_image_asset_${path}` : `blog_hero_main_focal_point_image_${path}`
					}
				/>
				{/* Text Overlay */}
				<Box sx={mainPhotoTextOverlayStyles} data-test-id="blog_hero_text_overlay">
					<Typography variant="heroMedium" color={'text.light'} data-test-id="blog_hero_text_overlay_title">
						{mainBlog.title ?? ''}
					</Typography>
					{!isMobileScreen && mainBlogSummary && (
						<RichText content={mainBlogSummary} docProps={mainPhotoRichTextDocProps} />
					)}
					<TAGLink
						href={blogItemLink}
						title={mainBlog.title ?? ''}
						dataTestId={`blog_hero_text_overlay_button_link_${path}`}
					>
						<TAGButton size="M" variant="secondaryHC" sx={buttonStyles}>
							{buttonLabel}
						</TAGButton>
					</TAGLink>
				</Box>
			</Box>

			{/* Remaining Photos Section */}

			<Box
				sx={remainingPhotosContainerWrapperStyles(isMobileScreen)}
				data-test-id="blog_hero_smaller_photos_wrapper"
			>
				{remainingBlogs.map((remainingBlog: BlogDetailsPage) => {
					const path = remainingBlog?.path ?? '';
					const blogImage = remainingBlog?.blogImage as BlogImage;
					const imageAsset = remainingBlog?.imageAsset as TImageAsset;
					const blogItemLink = `${blogRootPath}${path}/`;
					const blogSummary = remainingBlog?.summary?.json as RichTextContent;

					return (
						<Box
							key={path}
							sx={smallPhotoWrapperStyles(isMobileScreen)}
							data-test-id={`blog_hero_smaller_photo_wrapper_${path}`}
						>
							<TAGLink
								href={blogItemLink}
								title={remainingBlog?.title ?? ''}
								dataTestId={`blog_hero_smaller_photo_link_${path}`}
							>
								<BlogImageCondition
									imageAsset={imageAsset}
									blogImage={blogImage}
									dataTestId={
										imageAsset
											? `blog_hero_smaller_photo_img_asset_${path}`
											: `blog_hero_smaller_photo_focal_point_image_${path}`
									}
								/>
								{/* Text Overlay */}
								<Box
									sx={smallPhotoTextOverlayStyles}
									data-test-id={`blog_hero_smaller_photo_text_overlay_${path}`}
								>
									<Typography
										variant="header4"
										color="text.light"
										data-test-id={`blog_hero_smaller_photo_title_${path}`}
									>
										{remainingBlog?.title ?? ''}
									</Typography>
									{!isMobileScreen && blogSummary && (
										<RichText content={blogSummary} docProps={smallPhotoRichTextDocProps} />
									)}
								</Box>
							</TAGLink>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

export default BlogsListHero;
